// Third-party
import React from 'react';
import camelCase from 'lodash/camelCase';
import debounce from 'lodash/debounce';
import { connect } from 'react-redux';
import { Waypoint } from 'react-waypoint';

// Components
import DataSourceTabs from '@/views/components/data-source/DataSourceTabs';
import EditContactModal from './EditContactModal';
import { Input, SelectOption, SearchSelectInput } from '../inputs';
import { ContactPastille } from '../misc';
import { SecondaryLink } from '../buttons';
import { LoadingSpinner } from '../common';

// Enums
import { DATA_SOURCE_TAB_CATEGORY } from '@/enums/DataSource';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import { getContactName } from '../../../utils/contacts';
import { resetLocalStoragePersistenceSource } from '@/utils/persistence';
import localStorageUtils from '@/utils/local-storage';
import { getFilteredDataSourceTabs } from '@/utils/data-source';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { getMyContacts, GET_CONTACTS, setPersistenceSource } from '../../../redux/modules/contacts';
import { startLoaderAction, selectIsLoadingEvery } from '../../../redux/modules/ui/loaders';
import { setIsActiveContactPostMessage } from '@/redux/modules/post-message';

// Types
import GenericContact from '@/types/generic-contact';
import ReducedComparison from '@/types/reduced-comparison';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import DefaultModal from './DefaultModal';
import { DataSourceTabs as DataSourceTabsType } from '@/types/app-config';
import { MODAL_SIZES } from '@/utils/constants';
import { SEARCH_DEBOUNCE_TIME } from '@/utils/debounce/constants';

type StateProps = {
  contacts: GenericContact[];
  isPostMessageActive: boolean;
  loadingContacts: boolean;
  nextContactsToken?: string;
};

type DispatchProps = {
  getMyContacts: (...args: Parameters<typeof getMyContacts>) => Promise<GenericContact[]>;
  startLoaderAction: (name: string) => void;
  setIsActiveContactPostMessage: (isActive: boolean) => void;
  setPersistenceSource: (user: User, key: string, value?: string) => void;
};

export type Props = StateProps &
  DispatchProps & {
    show: boolean;
    user: User;
    onCancel: () => void;
    onSave: (name: string, contact?: GenericContact) => void;
    comparison: ReducedComparison;
    isSaving?: boolean;
    requireContact?: boolean;
  };

type State = {
  activeDataSource: string;
  comparisonName: string;
  currentHouseholdId?: string;
  selectedContact?: GenericContact;
  showCreateContact: boolean;
  contactsSearchQuery: string;
};

class SaveComparisonModal extends React.Component<Props> {
  private dataSourceTabs: DataSourceTabsType[] | null = getFilteredDataSourceTabs(
    this.props.user,
    DATA_SOURCE_TAB_CATEGORY.CONTACTS,
    this.props.isPostMessageActive,
  );

  private nameInput = React.createRef<Input>();

  readonly state: State = {
    activeDataSource: this.dataSourceTabs?.[0]?.value ?? '',
    comparisonName: this.props.comparison.name || formatMessage('Comparison.NamePlaceholder'),
    currentHouseholdId: this.props.comparison.householdId,
    selectedContact: this.getSelectedContact(),
    showCreateContact: false,
    contactsSearchQuery: '',
  };

  debouncedSearchContacts = debounce(() => {
    const { contactsSearchQuery } = this.state;
    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: undefined });
  }, SEARCH_DEBOUNCE_TIME);

  componentDidMount() {
    const { isPostMessageActive, user, getMyContacts, setIsActiveContactPostMessage } = this.props;

    // Reset the Persistence Source when the component mounts
    const isPostMessageDataSourceTabExist = this.dataSourceTabs?.find(tab => tab.isPostMessage);
    const isInitialDataSourceTabPostMessage = isPostMessageActive && isPostMessageDataSourceTabExist;

    if (isInitialDataSourceTabPostMessage) {
      setIsActiveContactPostMessage(false);
    }
    resetLocalStoragePersistenceSource(localStorageUtils.STORAGE_KEYS.CONTACT_PORTFOLIO_PERSISTENCE_SOURCE, user, () => getMyContacts());

    if (this.nameInput.current) {
      this.nameInput.current.selectAll();
    }
  }

  onContactsSearchChange = (contactsSearchQuery: string) => {
    this.setState({ contactsSearchQuery });
    this.props.startLoaderAction(GET_CONTACTS.type);

    this.debouncedSearchContacts();
  };

  onLoadMoreContacts = () => {
    const { contactsSearchQuery } = this.state;
    const { nextContactsToken } = this.props;

    this.props.getMyContacts({ query: contactsSearchQuery, nextToken: nextContactsToken });
  };

  getSelectedContact() {
    const { comparison, contacts } = this.props;

    if (!comparison.contact || !contacts) {
      return undefined;
    }

    return contacts.find(c => c._id === comparison.contact!._id);
  }

  onConfirm = () => {
    const { selectedContact, comparisonName } = this.state;
    this.props.onSave(comparisonName, selectedContact);
  };

  onContactChange = (contact?: Nullable<GenericContact>) => {
    if (contact !== null) {
      this.setState({ selectedContact: contact });
    } else {
      this.showCreateContactModal();
    }
  };

  onComparisonNameChange = (comparisonName: string) => {
    this.setState({ comparisonName });
  };

  showCreateContactModal = () => {
    this.setState({ showCreateContact: true });
  };

  onCancelCreateContact = () => {
    this.setState({ showCreateContact: false });
  };

  onCreateContact = (contact: GenericContact) => {
    this.setState({
      selectedContact: contact,
      showCreateContact: false,
    });
  };

  getSaveLabel = () => {
    const { requireContact } = this.props;
    const { selectedContact } = this.state;

    if (requireContact || selectedContact) {
      return formatMessage('Comparison.SaveComparisonModal.SaveToContact');
    }

    return formatMessage('Comparison.SaveComparisonModal.SaveAsDraft');
  };

  getModalTitle = (currentHouseholdId: string) => {
    if (currentHouseholdId) {
      return <FormattedMessage id="Comparison.SaveComparisonModal.SaveToHousehold" />;
    }
    return <FormattedMessage id="Comparison.SaveComparisonModal.SaveToContact" />;
  };

  getAdditionalDropdownActionsBefore = () => {
    const { selectedContact } = this.state;
    const { user } = this.props;
    const actions = [];

    const removeContactOption = (
      <SelectOption key="remove-contact" value={undefined} className="d-flex align-items-center">
        <SecondaryLink noBorder>
          <span className="remove-contact">
            <FormattedMessage id="Comparison.SaveComparisonModal.RemoveContact" />
          </span>
        </SecondaryLink>
      </SelectOption>
    );

    if (selectedContact) {
      actions.push(removeContactOption);
    }

    const hasPersistenceKeySource = localStorageUtils.getPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE) != null;

    if (!this.props.isPostMessageActive && !hasPersistenceKeySource) {
      const createContactOption = (
        <SelectOption key="new-contact" value={null}>
          <span className="d-flex align-items-center">
            <i className="icon-create-contact" />
            <span className="contact-name">
              <FormattedMessage id="Comparison.SaveComparisonModal.CreateContact" />
            </span>
          </span>
        </SelectOption>
      );

      actions.push(createContactOption);
    }

    return actions;
  };

  getAdditionalDropdownActionsAfter = () => {
    const { nextContactsToken } = this.props;

    const options = [];

    if (nextContactsToken != null) {
      options.push(
        <>
          <LoadingSpinner />
          <Waypoint onEnter={this.onLoadMoreContacts} />
        </>,
      );
    }

    return options;
  };

  onSelectDropdown = (contact: GenericContact) => {
    this.onContactChange(contact);
  };

  filterContact = (): boolean =>
    // Filter is handled at the data side - consider all given options to be valid.
    true;

  onSelectDataSource = (dataSourceValue: string) => {
    const { user, setIsActiveContactPostMessage, setPersistenceSource } = this.props;

    if (!this.dataSourceTabs) return;

    this.setState({ activeDataSource: dataSourceValue });

    const activeDataSource = this.dataSourceTabs.find(tab => tab.value === dataSourceValue);
    let persistenceSource;

    if (activeDataSource?.isPostMessage) {
      setIsActiveContactPostMessage(true);
    } else {
      setIsActiveContactPostMessage(false);
      persistenceSource = activeDataSource?.persistenceKey;
    }

    setPersistenceSource(user, localStorageUtils.STORAGE_KEYS.CONTACT_PERSISTENCE_SOURCE, persistenceSource);
    this.debouncedSearchContacts();
  };

  getModalBody = () => {
    const { contacts, loadingContacts } = this.props;
    const { comparisonName, currentHouseholdId } = this.state;

    const toggle = this.dataSourceTabs ? (
      <DataSourceTabs disabled={loadingContacts} tabs={this.dataSourceTabs} value={this.state.activeDataSource} onChange={this.onSelectDataSource} />
    ) : undefined;

    return (
      <div className="save-comparison-body">
        <div>
          {!currentHouseholdId && <FormattedMessage id="Comparison.SaveComparisonModal.Title" />}
          {currentHouseholdId && <FormattedMessage id="Comparison.SaveComparisonModal.TitleForHousehold" />}
        </div>
        <Input
          data-private
          data-dd-privacy="mask"
          ref={this.nameInput}
          value={comparisonName}
          onValueChange={this.onComparisonNameChange}
          placeholder={formatMessage('Comparison.SaveComparisonModal.NamePlaceholder')}
          label={formatMessage('Comparison.SaveComparisonModal.ComparisonName')}
        />

        {!currentHouseholdId && (
          <SearchSelectInput
            onSelect={this.onSelectDropdown}
            data-private
            data-dd-privacy="mask"
            placeholder={formatMessage('Comparison.SaveComparisonModal.SelectContactTitle')}
            filterOption={this.filterContact}
            additionnalOptionsBefore={this.getAdditionalDropdownActionsBefore()}
            additionnalOptionsAfter={this.getAdditionalDropdownActionsAfter()}
            selectedOptionValue={this.state.selectedContact}
            getTextForOptionValue={getContactName}
            onSearch={this.onContactsSearchChange}
            loadingResults={this.props.loadingContacts}
            label={formatMessage('Comparison.SaveComparisonModal.Contact')}
            toggleControl={toggle}
          >
            {contacts.map(c => (
              <SelectOption key={c._id} value={c} className="d-flex align-items-center">
                <ContactPastille contact={c} />
                <span className="contact-name">{getContactName(c)}</span>
              </SelectOption>
            ))}
          </SearchSelectInput>
        )}
      </div>
    );
  };

  render() {
    const { onSave, contacts, isSaving, requireContact, user, ...rest } = this.props;
    const { currentHouseholdId, comparisonName, selectedContact } = this.state;

    const saveIsDisabled = isSaving || !comparisonName || (requireContact && !selectedContact);

    return (
      <>
        <DefaultModal
          title={<span>{this.getModalTitle(currentHouseholdId)}</span>}
          onConfirm={this.onConfirm}
          body={this.getModalBody()}
          className="save-contact-modal"
          primaryIsDisabled={saveIsDisabled}
          actionsInverted
          confirmLabel={formatMessage('Comparison.SaveComparisonModal.Save')}
          size={MODAL_SIZES.SM}
          {...rest}
        />

        <EditContactModal user={user} show={this.state.showCreateContact} onCancel={this.onCancelCreateContact} onSave={this.onCreateContact} />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  contacts: state.contacts.contacts,
  nextContactsToken: state.contacts.nextContactsToken,
  loadingContacts: selectIsLoadingEvery(state, [GET_CONTACTS.type]),
  isPostMessageActive: state.postMessage.isPostMessageActive,
});

export default connect(mapStateToProps, {
  getMyContacts,
  startLoaderAction,
  setIsActiveContactPostMessage,
  setPersistenceSource,
})(SaveComparisonModal);
